'use client';

import { type FC, useEffect, useState } from 'react';
import type { Team, RushMidDropAward } from '@cs-shot/battle-sim';
import { cn } from '../../../utils/cn';
import { Credits } from '../../Credits';
import { ItemCard } from '../../ItemCard';
import { Avatar } from '../../ui';
import { sideColor } from './sideColor';
import { rushMidAvatarUrl } from './avatarUrl';
import { useLocalTranslation } from '../../../hooks/useLocalTranslation';
import translations from './RushMid.translations';

export interface RevealDrop {
  fighterId: string;
  name: string;
  team: Team;
  drop: RushMidDropAward;
}

const splitName = (name: string): { firstName: string; lastName?: string } => {
  const [first, ...rest] = name.trim().split(/\s+/);
  return { firstName: first, lastName: rest.length ? rest.join(' ') : undefined };
};

const PER_DROP_MS = 2200;

export const DropReveal: FC<{ drops: RevealDrop[]; onDone: () => void }> = ({ drops, onDone }) => {
  const { t } = useLocalTranslation(translations);
  const [i, setI] = useState(0);

  const advance = () => setI((n) => n + 1);

  useEffect(() => {
    if (i >= drops.length) {
      onDone();
      return;
    }
    const id = window.setTimeout(advance, PER_DROP_MS);
    return () => window.clearTimeout(id);
  }, [i, drops.length, onDone]);

  if (i >= drops.length) return null;

  const cur = drops[i];
  const { drop } = cur;
  const { firstName, lastName } = splitName(cur.name);

  return (
    <button
      type="button"
      onClick={advance}
      className="flex w-full flex-col items-center justify-center pt-6 pb-12 text-center"
      aria-label={t('dropRevealTapHint')}
    >

      <div key={i} className="animate-shot-drop-reveal w-full text-start">
        <ItemCard
          imageUrl={drop.image ?? ''}
          name={drop.name}
          rarity={drop.rarity}
          rarityColor={drop.rarityColor}
        />
      </div>

      <div key={`meta-${i}`} className="animate-shot-drop-meta-in mt-6 flex flex-col items-center gap-2">
        <div className="rounded-xl bg-side-t/15 px-3 py-1.5 text-lg font-extrabold text-side-t">
          <Credits amount={drop.priceCredits} iconSize={18} />
        </div>
        <div className="mt-3 flex items-center gap-2">
          <Avatar size="s" src={rushMidAvatarUrl(drop.userId)} fallbackKey={drop.userId ?? cur.fighterId} firstName={firstName} lastName={lastName} />
          <span className={cn('font-bold', sideColor(cur.team, 'text'))}>{cur.name}</span>
        </div>
      </div>
    </button>
  );
};
